'use client'

// Drawer tabs. Replaces the title and strapline, because a drawer with four
// sections does not need to be told it is about you: the tabs say so, and
// the strapline was the first thing to go when the space got tight.
//
// Each tab can carry its own coloured dot, matching the one on the corner
// button, so "something is waiting" and "it is waiting HERE" are the same
// piece of information seen twice rather than two things to reconcile.

import { kawaii, ui } from '../../lib/ui/theme'
import { NoticeDot } from './Notices'

export function Tabs({ tabs, active, onPick }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" role="tablist">
      {tabs.map((t) => {
        const on = t.id === active
        // A locked tab still shows: seeing what standing would get you is
        // the point of a ladder you climb. It just cannot be opened, and it
        // reports why rather than absorbing the click in silence.
        const locked = !!t.disabled
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={on}
            aria-disabled={locked || undefined}
            title={locked ? t.disabledHint || undefined : undefined}
            onClick={() => onPick(t.id, locked)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 14px',
              borderRadius: 999,
              // The selected one is a real pill; the rest are just words, so
              // the row reads as one control rather than five buttons.
              background: on ? kawaii.ink : 'transparent',
              color: on ? kawaii.cream : kawaii.inkSoft,
              border: on ? kawaii.outlineThin : '2px solid transparent',
              fontFamily: ui.font,
              fontSize: 14.5,
              fontWeight: 800,
              opacity: locked ? 0.45 : 1,
              cursor: locked ? 'not-allowed' : on ? 'default' : 'pointer',
              transition: 'background 160ms ease, color 160ms ease',
            }}
          >
            {t.label}
            {locked && <LockGlyph />}
            {/* A dot on a locked tab is the point, not a mistake. Something
                is waiting behind that door and the lock is the reason you
                cannot open it yet; hiding the dot hid the only thing that
                makes the rung worth climbing. Clicking it still explains
                what it would take. */}
            {t.notice && <NoticeDot kind={t.notice} />}
          </button>
        )
      })}
    </div>
  )
}

/** Small closed padlock. Drawn rather than typed: no emoji anywhere. */
function LockGlyph() {
  return (
    <svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M2.6 5V3.4a2.4 2.4 0 0 1 4.8 0V5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="1" y="5" width="8" height="6.2" rx="1.6" fill="currentColor" />
    </svg>
  )
}

export default Tabs
